import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Input from '../../Components/Input/Input';
import Button from '../../Components/Button/Button';
import { handleValidationErrors, validateForm, validatePassword } from '../../Utils/validationHelper';
import { postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Create refs for all input fields
  const inputRefs = {
    newPassword: useRef(null),
    confirmPassword: useRef(null),
  };

  useEffect(() => {
    // Check if token or email is present (from reset password link)
    const token = location.search.split('token=')[1]?.split('&')[0];
    const email = location.state?.email || localStorage.getItem('resetPasswordEmail');
    
    if (!token && !email) {
      // If no token or email, redirect to forgot password
      toast.error('Invalid reset password link');
      navigate('/forgot-password');
    }

    // Add animation classes on mount
    const infoSection = document.querySelector('.signup-info');
    const formSection = document.querySelector('.login-form-section');
    const formCard = document.querySelector('.login-form-card');
    
    if (infoSection) infoSection.classList.add('animate-fade-in-left');
    if (formSection) formSection.classList.add('animate-fade-in-right');
    if (formCard) formCard.classList.add('animate-scale-in');
  }, [location, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Prevent leading spaces for password inputs
    if (name === 'newPassword' || name === 'confirmPassword') {
      if (newValue.startsWith(' ')) {
        newValue = newValue.trimStart();
      }
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
    
    // Clear errors for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    
    // Clear confirm password error if passwords match
    if (name === 'confirmPassword' && errors.confirmPassword && value === formData.newPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
    if (name === 'newPassword' && errors.confirmPassword && value === formData.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Define validation rules
    const validationRules = {
      newPassword: {
        required: true,
        custom: (value) => {
          return validatePassword(value);
        },
        message: {
          required: 'New password is required',
        },
      },
      confirmPassword: {
        required: true,
        message: {
          required: 'Please confirm your password',
        },
      },
    };

    // Validate the form
    const newErrors = validateForm(formData, validationRules);

    // Custom validation: Check if passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Handle errors (will focus on first error input)
    if (handleValidationErrors(newErrors, inputRefs, setErrors)) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const token = location.search.split('token=')[1]?.split('&')[0];
      const email = location.state?.email || localStorage.getItem('resetPasswordEmail');

      // TODO: Replace with actual reset password API endpoint
      const response = await postApi('/auth/reset-password', {
        token: token,
        password: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });

      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Password reset successfully');
        // Clear reset password email from storage
        localStorage.removeItem('resetPasswordEmail');
        // Navigate to login
        navigate('/login');
      } else {
        const errorMessage = response?.message || 'Password reset failed. Please try again.';
        setErrors({ submit: errorMessage });
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Password reset failed. Please try again.';
      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="signup-info">
          <div className="signup-logo">
            <Link to="/" onClick={() => localStorage.clear()}>
              <img src={window.getPublicUrl("/assets/images/Logo.png")} alt="ClearRent" className="signup-logo-img" />
            </Link>
          </div>
          <h1 className="signup-title">Simplifying tenant Screening for landlords</h1>
          <p className="signup-subtitle">
            Create your account and get instant access to automated tenant screening.
          </p>
          <ul className="signup-features">
            <li>
              <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="signup-feature-icon" />
              <span>Unlimited property listings</span>
            </li>
            <li>
              <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="signup-feature-icon" />
              <span>Automated voice interviews & document verification</span>
            </li>
            <li>
              <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="signup-feature-icon" />
              <span>Automated reference checks</span>
            </li>
          </ul>
        </div>
        <div className="login-form-section">
          <h2 className="login-form-title">Reset Password</h2>
          <p className="login-form-subtitle">
            Enter your new password to reset your account password
          </p>
          <form onSubmit={handleSubmit} className={`login-form-card ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`} noValidate>
            <div className="login-form-header">Account Information</div>
            <div className={`login-form-fields ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`}>
              <div className="login-password-wrapper">
                <label htmlFor="newPassword" className="input-label">
                  New Password
                  <span className="required">*</span>
                </label>
                <div className="login-password-input-wrapper">
                  <input
                    ref={inputRefs.newPassword}
                    type={showNewPassword ? 'text' : 'password'}
                    id="newPassword"
                    name="newPassword"
                    placeholder="Eg. 45234kdk45"
                    value={formData.newPassword}
                    onChange={handleChange}
                    required
                    className={`input-field ${errors.newPassword ? 'input-error' : ''}`.trim()}
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    <img 
                      src={window.getPublicUrl ? window.getPublicUrl(showNewPassword ? "/assets/images/Eye.svg" : "/assets/images/eye-closed.svg") : (showNewPassword ? "/assets/images/Eye.svg" : "/assets/images/eye-closed.svg")} 
                      alt={showNewPassword ? "Hide password" : "Show password"} 
                      className="login-eye-icon"
                    />
                  </button>
                </div>
                <div className={`error-message-container ${errors.newPassword ? 'has-error' : ''}`}>
                  {errors.newPassword && <span className="error-message">{errors.newPassword}</span>}
                </div>
              </div>
              <div className="login-password-wrapper">
                <label htmlFor="confirmPassword" className="input-label">
                  Confirm Password
                  <span className="required">*</span>
                </label>
                <div className="login-password-input-wrapper">
                  <input
                    ref={inputRefs.confirmPassword}
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="Eg. 45234kdk45"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className={`input-field ${errors.confirmPassword ? 'input-error' : ''}`.trim()}
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <img 
                      src={window.getPublicUrl ? window.getPublicUrl(showConfirmPassword ? "/assets/images/Eye.svg" : "/assets/images/eye-closed.svg") : (showConfirmPassword ? "/assets/images/Eye.svg" : "/assets/images/eye-closed.svg")} 
                      alt={showConfirmPassword ? "Hide password" : "Show password"} 
                      className="login-eye-icon"
                    />
                  </button>
                </div>
                <div className={`error-message-container ${errors.confirmPassword ? 'has-error' : ''}`}>
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                </div>
              </div>
            </div>
            {errors.submit && (
              <div className="error-message-container has-error" style={{ marginBottom: '1rem' }}>
                <span className="error-message">{errors.submit}</span>
              </div>
            )}
            <Button 
              type="submit" 
              variant="success" 
              className="login-submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
            <p className="login-signup-link">
              Remember your password? <Link to="/login">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
